import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    DashboardTitle,
    DashboardWrapper,
    HierarchyFlowChart,
    HierarchyForm,
    CactusModal,
} from '../../components';

import Option from '../../models/Option';
import HierarchyModel, { getSubordinate, getHierarchyOption, OrganizationChartData, getOrganizationChartData } from '../../models/Hierarchy';
import { Paginate } from '../../services/PaginateResponse';
import RegisterFormPremium, { RegisterFormInputs } from '../../components/RegisterFormPremium/RegisterFormPremium';
import { RightPanel } from '..';
import User, { getFullName } from '../../models/User';
import * as actionCreator from '../../actions';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUsers, faSitemap, faLayerGroup, faCalendar, faCodeBranch } from '@fortawesome/free-solid-svg-icons';

interface HierarchyProps {};

const _getInitials = (user: HierarchyModel): string => {
    const first = user.name?.charAt(0)?.toUpperCase() ?? '';
    const last = user.last_name?.charAt(0)?.toUpperCase() ?? '';
    return `${first}${last}`;
};

const _formatDate = (dateStr: string): string => {
    try {
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    } catch {
        return dateStr;
    }
};

/** Count maximum depth of the tree */
const _countDepth = (node: OrganizationChartData, d = 0): number =>
    node.children.length === 0
        ? d
        : Math.max(...node.children.map(c => _countDepth(c, d + 1)));

/** Count ALL nodes in the tree (root + all descendants) */
const _countNodes = (node: OrganizationChartData): number =>
    1 + node.children.reduce((sum, c) => sum + _countNodes(c), 0);

const Hierarchy: React.FC<HierarchyProps> = () => {

    const [t] = useTranslation();
    const dispatch = useDispatch();

    const authReducer = useSelector((store: any) => store.authReducer);
    const isLoadingAuth: boolean = authReducer?.isLoading;

    const userReducer = useSelector((store: any) => store.userReducer);
    const isLoadingUser: boolean = userReducer?.isLoading;

    const hierarchyReducer  = useSelector((store: any) => store.hierarchyReducer);
    const isLoadingHierarchy: boolean                           = hierarchyReducer?.isLoading     ?? false;
    const isLoadingMore:      boolean                           = hierarchyReducer?.isLoadingMore  ?? false;
    const rootUser:           HierarchyModel | null             = hierarchyReducer?.rootUser       ?? null;
    const children:           Paginate<HierarchyModel[]> | null = hierarchyReducer?.children       ?? null;

    const [showNewUserModal, setShowNewUserModal] = useState<boolean>(false);
    const [nodeSelected, setNodeSelected]         = useState<OrganizationChartData | null>(null);

    /** Combine rootUser + loaded children into a synthetic Hierarchy tree */
    const syntheticRoot = useMemo<HierarchyModel | null>(() => {
        if (!rootUser) return null;
        return { ...rootUser, nested_children: children?.data ?? [] };
    }, [rootUser, children]);

    const dataSource = useMemo(() => getOrganizationChartData(syntheticRoot), [syntheticRoot]);

    const subordinates = useMemo<Option[]>(() => {
        if (!dataSource) return [];
        const all: HierarchyModel[] = [dataSource.data, ...getSubordinate(dataSource.data)];
        return all.map(d => getHierarchyOption(d));
    }, [dataSource]);

    useEffect(() => {
        _fetchHierarchy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _fetchHierarchy = () => {
        dispatch(actionCreator.hierarchyFetch());
        _handleClosePanel();
    };

    const _handleClosePanel = () => {
        setNodeSelected(null);
        setShowNewUserModal(false);
    };

    // ─── Stats ──────────────────────────────────────────────────────────────
    // Count every node in the loaded tree (root + all descendants recursively).
    // This correctly captures nested users that are NOT in children.total.
    const totalUsers = dataSource ? _countNodes(dataSource) : 0;
    const treeDepth  = dataSource ? _countDepth(dataSource) : 0;

    const _renderChart = () => {
        if (isLoadingHierarchy) {
            return (
                <div className="hf-state">
                    <div className="hf-state__spinner" />
                    <span className="hf-state__label">{t('Loading...')}</span>
                </div>
            );
        }

        if (!dataSource) {
            return (
                <div className="hf-state">
                    <FontAwesomeIcon icon={faSitemap} size="2x" className="hf-state__icon" />
                    <span className="hf-state__label">{t('You have no subordinates. Create a hierarchy first')}</span>
                </div>
            );
        }

        return (
            <HierarchyFlowChart
                // key changes only when the loaded children count grows,
                // forcing a remount (and a fresh fitView) after "Load more".
                // It intentionally does NOT change on node selection.
                key={`hf-${children?.data?.length ?? 0}`}
                dataSource={dataSource}
                selectedId={nodeSelected?.id as string ?? null}
                onNodeClick={(node) => setNodeSelected(node)}
                onPaneClick={_handleClosePanel}
            />
        );
    };

    return (
        <DashboardWrapper>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="d-flex align-items-start">
                <div id="center-panel" className="flex-grow-1">
                    <DashboardTitle
                        title={t('Hierarchy')}
                        isLoading={isLoadingHierarchy}
                        withRefreshIcon={!isLoadingHierarchy}
                        refreshIconCallBack={_fetchHierarchy}
                    />
                </div>
            </div>

            {/* ── Stats bar ───────────────────────────────────────────────── */}
            {dataSource && !isLoadingHierarchy && (
                <div className="hf-stats">
                    <div className="hf-stats__item">
                        <FontAwesomeIcon icon={faUsers} className="hf-stats__icon" />
                        <span className="hf-stats__value">{totalUsers}</span>
                        <span className="hf-stats__label">{t('Total users')}</span>
                    </div>
                    <div className="hf-stats__divider" />
                    <div className="hf-stats__item">
                        <FontAwesomeIcon icon={faLayerGroup} className="hf-stats__icon" />
                        <span className="hf-stats__value">{treeDepth + 1}</span>
                        <span className="hf-stats__label">{t('Levels')}</span>
                    </div>
                </div>
            )}

            {/* ── Chart ───────────────────────────────────────────────────── */}
            <div className="hf-chart">
                {_renderChart()}

                {/* ── Load more (overlay at chart bottom) ─────────────────── */}
                {children?.next_page_url && !isLoadingHierarchy && (
                    <div className="hf-load-more">
                        <button
                            className={`btn btn-sm btn-outline-secondary ${isLoadingMore ? 'btn-cactus--loading' : ''}`}
                            disabled={isLoadingMore}
                            onClick={() => {
                                dispatch(actionCreator.hierarchyFetchNext((children.current_page ?? 1) + 1));
                            }}
                        >
                            {isLoadingMore ? t('Loading...') : t('Load more')}
                        </button>
                    </div>
                )}
            </div>

            {/* ── Right Panel ─────────────────────────────────────────────── */}
            <RightPanel opacity={nodeSelected ? 1 : 0}>

                {/* ── User header ────────────────────────────────────────── */}
                <div className="d-flex align-items-start mb-3">
                    {nodeSelected && (
                        <div className="hf-panel-user">
                            <div className="hf-panel-user__avatar hf-panel-user__avatar--lg">
                                {_getInitials(nodeSelected.data)}
                            </div>
                            <div className="hf-panel-user__info">
                                <span className="hf-panel-user__name">{getFullName(nodeSelected.data)}</span>
                                <span className="hf-panel-user__email">{nodeSelected.data.email}</span>
                                {nodeSelected.data.rol?.length > 0 && (
                                    <div className="hf-panel-user__roles">
                                        {nodeSelected.data.rol.map((role: string) => (
                                            <span key={role} className={`hf-panel-user__badge hf-panel-user__badge--${role}`}>
                                                {role}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <div
                        className="ml-auto pointer p-2 flex-shrink-0"
                        onClick={() => {
                            if (isLoadingUser) return;
                            _handleClosePanel();
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            color={isLoadingUser ? 'var(--cds-border)' : 'var(--cds-text-muted)'}
                            size="1x"
                        />
                    </div>
                </div>

                {/* ── Meta row ───────────────────────────────────────────── */}
                {nodeSelected && (
                    <div className="hf-panel-meta mb-3">
                        <div className="hf-panel-meta__item">
                            <FontAwesomeIcon icon={faCodeBranch} className="hf-panel-meta__icon" />
                            <span className="hf-panel-meta__value">{nodeSelected.children.length}</span>
                            <span className="hf-panel-meta__label">{t('Subordinates')}</span>
                        </div>
                        {nodeSelected.data.created_at && (
                            <>
                                <div className="hf-panel-meta__divider" />
                                <div className="hf-panel-meta__item">
                                    <FontAwesomeIcon icon={faCalendar} className="hf-panel-meta__icon" />
                                    <span className="hf-panel-meta__label hf-panel-meta__label--date">
                                        {_formatDate(nodeSelected.data.created_at)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── Section divider ────────────────────────────────────── */}
                <div className="hf-panel-divider mb-3">
                    <span>{t('Edit User')}</span>
                </div>

                {/* ── Form ───────────────────────────────────────────────── */}
                <HierarchyForm
                    data={nodeSelected?.data ?? null}
                    subordinates={rootUser?.id === nodeSelected?.id ? [] : subordinates}
                    btnLoading={isLoadingUser}
                    onSubmit={(formData) => {
                        const updateUser: User = { ...nodeSelected!.data, ...formData };
                        dispatch(actionCreator.updateUser(updateUser, () => { _fetchHierarchy(); }));
                    }}
                />

                {/* Spacer so content doesn't hide under the fixed bottom button */}
                <div style={{ height: '56px' }} />

                <div className="p-3" style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                    <button
                        className={`btn btn-sm btn-cactus ${isLoadingUser ? 'btn-cactus--loading' : ''} w-100`}
                        onClick={() => setShowNewUserModal(true)}
                        disabled={isLoadingUser}
                    >
                        {t('Create User')}
                    </button>
                </div>

            </RightPanel>

            {/* ── Create User Modal ────────────────────────────────────────── */}
            <CactusModal
                show={showNewUserModal}
                title={t('Create User')}
                onClose={() => setShowNewUserModal(false)}
                width="70vw"
                disableOnClose={isLoadingAuth}
            >
                <RegisterFormPremium
                    showPremiumPartnerOption={false}
                    btnLoading={isLoadingAuth}
                    onSubmit={(formData: RegisterFormInputs) => {
                        const newUser: RegisterFormInputs = { ...formData, user_id: nodeSelected?.data.id };
                        dispatch(actionCreator.userCreate(newUser, (isCreated: boolean) => {
                            if (isCreated) _fetchHierarchy();
                        }));
                    }}
                />
            </CactusModal>

        </DashboardWrapper>
    );

};

export default React.memo(Hierarchy);
